import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CalendarDays, MapPin, Phone } from "lucide-react";
import { BUSINESS } from "../lib/business";
import { Eyebrow, MaskLines, Reveal } from "../components/Motion";
import { OpenBadge } from "../components/OpenBadge";

export const Workshop = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section id="hours" ref={ref} data-testid="hours-section" className="mx-auto max-w-[1440px] px-5 py-24 md:px-10 md:py-36">
      <div className="grid gap-12 lg:grid-cols-12 lg:items-center">
        <div className="relative overflow-hidden lg:col-span-6">
          <div className="clip-notch relative aspect-[4/5] overflow-hidden md:aspect-[5/4] lg:aspect-[4/5]">
            <motion.img style={{ y }} src="/img/bench.jpg" alt="Wilko Tech repair workbench" className="absolute inset-0 h-[124%] w-full -top-[12%] object-cover" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,transparent_20%,rgba(9,13,22,0.75)_80%)]" />
          </div>
        </div>
        <div className="lg:col-span-6 lg:pl-10">
          <Eyebrow>03 — Bench hours</Eyebrow>
          <MaskLines className="mt-5 font-display text-5xl font-black uppercase leading-[0.9] tracking-tight sm:text-6xl lg:text-8xl" lines={["7am", <span key="t" className="text-ember">— 6pm</span>]} />
          <Reveal className="mt-8 max-w-md">
            <OpenBadge />
            <p className="mt-6 text-slate-300">
              Pick-ups run during opening hours, or you can post your machine to us. You can submit a repair request online at any time.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-px bg-white/[0.08]">
              <div className="bg-ink p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Opens</p>
                <p className="mt-2 font-display text-4xl font-extrabold">07:00</p>
              </div>
              <div className="bg-ink p-6">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">Closes</p>
                <p className="mt-2 font-display text-4xl font-extrabold">18:00</p>
              </div>
            </div>
            <div className="mt-px grid gap-px bg-white/[0.08] sm:grid-cols-3" data-testid="workshop-contact-details">
              <div className="bg-ink p-5">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400"><CalendarDays className="h-3.5 w-3.5" /> Days</p>
                <p data-testid="workshop-days" className="mt-2 text-sm text-bone">{BUSINESS.days}</p>
              </div>
              <div className="bg-ink p-5">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400"><MapPin className="h-3.5 w-3.5" /> Based in</p>
                <p data-testid="workshop-address" className="mt-2 text-sm text-bone">{BUSINESS.address}</p>
              </div>
              <div className="bg-ink p-5">
                <p className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400"><Phone className="h-3.5 w-3.5" /> Call</p>
                <a href={BUSINESS.phoneHref} data-testid="workshop-phone" className="mt-2 block text-sm text-bone hover:text-ember">{BUSINESS.phone}</a>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
      <Reveal className="mt-24 border border-white/[0.08] bg-ember p-8 text-ink md:mt-36 md:p-14">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <h2 className="font-display text-4xl font-black uppercase leading-[0.9] tracking-tight md:text-7xl">Ready when<br />you are.</h2>
          <Link to="/request" data-testid="cta-request-repair-btn" className="group inline-flex items-center justify-center gap-3 bg-ink px-8 py-5 font-semibold text-bone transition-transform duration-300 active:scale-95">
            Start a repair request <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>
      </Reveal>
    </section>
  );
};
